import React from "react";
import { FaCheck, FaExclamation, FaI, FaSpinner, FaX } from "react-icons/fa6";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { IconButton } from "../components";

interface DialogProps {
  id: string;
  body?: React.ReactNode;
  dismissible?: boolean;
  width?: string | number;
  centered?: boolean;
  onDismiss?: () => void;
}

const Dialog: React.FC<DialogProps> = ({ body, dismissible = true, width = "20rem", centered = true, onDismiss }) => {
  const handleClose = () => {
    if (!dismissible) return;
    onDismiss?.();
  };

  const contentStyle: React.CSSProperties = {
    width: typeof width === "number" ? `${width}px` : width,
    maxWidth: "calc(100vw - 2rem)",
  };

  const alignmentClass = centered ? "items-center" : "items-start py-12";

  return (
    <div className={`fixed inset-0 z-50 flex justify-center px-4 ${alignmentClass}`} role="presentation">
      <div className="absolute inset-0 bg-black/25 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-full" onClick={handleClose}>
        <div
          className="relative mx-auto rounded-3xl bg-card text-card-foreground"
          style={contentStyle}
          role="dialog"
          aria-modal="true"
          onClick={(event) => event.stopPropagation()}
        >
          {/* {dismissible && (
            <div className="absolute right-2 top-2">
              <IconButton
                type="button"
                icon={FaX}
                onClick={handleClose}
              />
            </div>
          )} */}
          {body}
        </div>
      </div>
    </div>
  );
};

interface ToastProps {
  id: string;
  title?: string;
  description?: string;
  variant?: "success" | "danger" | "warning" | "info";
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ title, description, variant, duration = 3000 }) => {
  const { t } = useTranslation();
  const [exiting, setExiting] = React.useState(false);

  React.useEffect(() => {
    // Start exit animation 150ms before removal
    const exitTimeout = setTimeout(() => setExiting(true), duration - 300);
    return () => clearTimeout(exitTimeout);
  }, [duration]);

  const variantClass = () => {
    switch (variant) {
      case "success":
        return "border-green-500 bg-green-100 text-black";
      case "danger":
        return "border-red-500 bg-red-100 text-black";
      case "warning":
        return "border-amber-500 bg-amber-100 text-black";
      case "info":
        return "border-blue-500 bg-blue-100 text-black";
    }
  };

  const variantIcon = () => {
    switch (variant) {
      case "success":
        return (
          <div className="p-2 rounded-full text-[1.5rem] bg-green-200 text-green-500">
            <FaCheck />
          </div>
        );
      case "danger":
        return (
          <div className="p-2 rounded-full text-[1.5rem] bg-red-200 text-red-500">
            <FaExclamation />
          </div>
        );
      case "warning":
        return (
          <div className="p-2 rounded-full text-[1.5rem] bg-amber-200 text-amber-500">
            <FaExclamation />
          </div>
        );
      case "info":
        return (
          <div className="p-2 rounded-full text-[1.5rem] bg-blue-200 text-blue-500">
            <FaI />
          </div>
        );
    }
  };

  return (
    <div
      className={[
        "max-w-full w-md border rounded-2xl p-3 shadow-md flex items-start gap-2",
        variantClass(),
        `slide-in ${exiting ? "slide-out" : ""}`,
      ].join(" ")}
    >
      {variantIcon()}
      <div>
        <div className="font-semibold text-lg">{title ?? t(`common.toast.title.${variant}`)}</div>
        {description && <div className="text-sm mt-1">{description.toString()}</div>}
      </div>
    </div>
  );
};

interface OverlayContextType {
  loading: boolean;
  setLoading: (state: boolean) => void;
  toastList: ToastProps[];
  addToast: (toast: Omit<ToastProps, "id">) => void;
  clearToast: () => void;
  addDialog: (dialog: Omit<DialogProps, "id">) => void;
  removeLastDialog: () => void;
  clearDialog: () => void;
}

const OverlayContext = React.createContext<OverlayContextType | undefined>(undefined);

const dialogReducer = (
  state: DialogProps[],
  action:
    | { type: "ADD_DIALOG"; payload: DialogProps }
    | { type: "REMOVE_DIALOG"; payload: { id: string } }
    | { type: "REMOVE_LAST_DIALOG" }
    | { type: "CLEAR_DIALOG" },
): DialogProps[] => {
  switch (action.type) {
    case "ADD_DIALOG":
      return [...state, action.payload];
    case "REMOVE_DIALOG":
      return state.filter((d) => d.id !== action.payload.id);
    case "REMOVE_LAST_DIALOG":
      return state.slice(0, -1);
    case "CLEAR_DIALOG":
      return [];
    default:
      return state;
  }
};

const toastReducer = (
  state: ToastProps[],
  action:
    | { type: "ADD_TOAST"; payload: ToastProps }
    | { type: "REMOVE_TOAST"; payload: { id: string } }
    | { type: "CLEAR_TOAST" },
): ToastProps[] => {
  switch (action.type) {
    case "ADD_TOAST":
      return [...state, action.payload];
    case "REMOVE_TOAST":
      return state.filter((t) => t.id !== action.payload.id);
    case "CLEAR_TOAST":
      return [];
    default:
      return state;
  }
};

export const OverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [dialogList, updateDialogList] = React.useReducer(dialogReducer, []);
  const [toastList, updateToastList] = React.useReducer(toastReducer, []);

  const addToast = (toast: Omit<ToastProps, "id">) => {
    const id = dayjs().toISOString();
    updateToastList({ type: "ADD_TOAST", payload: { ...toast, id } });

    setTimeout(() => removeToast(id), toast.duration ?? 3000);
  };

  const removeToast = (id: string) => {
    updateToastList({ type: "REMOVE_TOAST", payload: { id } });
  };

  const clearToast = () => {
    updateToastList({ type: "CLEAR_TOAST" });
  };

  const addDialog = (dialog: DialogProps) => {
    const id = dayjs().toISOString();
    updateDialogList({
      type: "ADD_DIALOG",
      payload: {
        ...dialog,
        id,
        onDismiss: () => {
          removeDialog(id);
          dialog.onDismiss?.();
        },
      },
    });
  };

  const removeDialog = (id: string) => {
    updateDialogList({ type: "REMOVE_DIALOG", payload: { id } });
  };

  const removeLastDialog = () => {
    updateDialogList({ type: "REMOVE_LAST_DIALOG" });
  };

  const clearDialog = () => {
    updateDialogList({ type: "CLEAR_DIALOG" });
  };

  return (
    <OverlayContext.Provider
      value={{ loading, setLoading, toastList, addToast, clearToast, addDialog, removeLastDialog, clearDialog }}
    >
      {children}

      {dialogList.map((dialog, idx) => (
        <Dialog key={`dialog-${idx}`} {...dialog} onDismiss={dialog.onDismiss ?? removeLastDialog} />
      ))}

      {loading && (
        <div className="fixed inset-0 flex items-center justify-center backdrop-blur-xs bg-white/15 z-50">
          <FaSpinner className="animate-spin text-primary text-4xl" />
        </div>
      )}

      <div className="fixed top-4 right-4 gap-2 z-50 flex flex-col items-end">
        {toastList.map((t) => (
          <Toast key={t.id} {...t} />
        ))}
      </div>
    </OverlayContext.Provider>
  );
};

export const useOverlay = (): OverlayContextType => {
  const ctx = React.useContext(OverlayContext);
  if (!ctx) throw new Error("useOverlay must be used inside OverlayProvider");
  return ctx;
};
